import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const purpose = params.get("purpose") || "author_annual";
  const [state, setState] = useState<"checking" | "paid" | "timeout" | "error">("checking");
  const attempts = useRef(0);
  const { refresh } = useAuth();

  const readBack = purpose.startsWith("premium") ? localStorage.getItem("nl_read_after_upgrade") : null;
  const clearReadBack = () => localStorage.removeItem("nl_read_after_upgrade");

  const copy = purpose.startsWith("premium")
    ? {
        title: "Welcome to Premium 👑",
        body: readBack
          ? "Exclusive analysis is unlocked and every ad just disappeared. The story you wanted is waiting for you."
          : "Exclusive analysis is unlocked and every ad on the site just disappeared. Enjoy the clean reading experience.",
        ctaText: readBack ? "Continue reading your story" : "Start reading",
        ctaTo: readBack ? `/article/${readBack}` : "/",
      }
    : purpose === "pr_submission"
      ? { title: "Press release received 📣", body: "Payment confirmed. Our editors are reviewing your release — it typically publishes within one business day.", ctaText: "View my submissions", ctaTo: "/pr" }
      : purpose.startsWith("ad_")
        ? { title: "Campaign submitted 🚀", body: "Payment confirmed. Your banner is with our team for a quick approval — it goes live the moment it's cleared.", ctaText: "View my campaigns", ctaTo: "/advertise" }
        : { title: "Payment confirmed 🎉", body: "Welcome to the Approved Author program. Your application is now with our editors — you'll unlock the Author Studio the moment you're approved.", ctaText: "View my status", ctaTo: "/author-program" };

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attempts.current += 1;
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState("paid");
          refresh().catch(() => {});
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) { setState("error"); return; }
      } catch {
        setState("error");
        return;
      }
      if (attempts.current >= 10) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="payment-success-page" className="max-w-xl mx-auto px-4 py-24 text-center">
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="h-10 w-10 text-[#00E5FF] animate-spin mx-auto mb-5" />
          <h1 className="font-heading font-bold text-white text-2xl">Confirming your payment…</h1>
          <p className="text-gray-400 mt-2">This usually takes a few seconds. Don't close this page.</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-paid">
          <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-5" />
          <h1 className="font-heading font-black text-white text-3xl">{copy.title}</h1>
          <p className="text-gray-400 mt-3">{copy.body}</p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to={copy.ctaTo} data-testid="payment-view-status" onClick={clearReadBack}
              className={`${readBack ? "sparkle-btn bg-gradient-to-r from-[#00A6E0] via-[#3B6CF6] to-[#7C3AED]" : "bg-[#7C3AED] hover:bg-[#6a2dd0]"} text-white font-bold px-6 py-2.5 rounded-md transition-colors`}>
              {copy.ctaText}
            </Link>
            <Link to="/" onClick={clearReadBack} className="border border-white/20 text-white px-6 py-2.5 rounded-md hover:bg-white/5 transition-colors">
              Back to news
            </Link>
          </div>
        </div>
      )}
      {state === "timeout" && (
        <div data-testid="payment-timeout">
          <AlertTriangle className="h-10 w-10 text-yellow-400 mx-auto mb-5" />
          <h1 className="font-heading font-bold text-white text-2xl">Still processing</h1>
          <p className="text-gray-400 mt-2">
            Your payment is taking longer than usual. Check <Link to="/author-program" className="text-[#00E5FF] underline">your status</Link> in a minute — we'll update it automatically.
          </p>
        </div>
      )}
      {state === "error" && (
        <div data-testid="payment-error">
          <AlertTriangle className="h-10 w-10 text-red-400 mx-auto mb-5" />
          <h1 className="font-heading font-bold text-white text-2xl">We couldn't confirm that payment</h1>
          <p className="text-gray-400 mt-2">
            If you were charged, your access will activate shortly. Otherwise{" "}
            <Link to="/author-program" className="text-[#00E5FF] underline">try again</Link>.
          </p>
        </div>
      )}
    </div>
  );
}
